import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";

const MAX_ATTEMPTS = 20; // 20 polls * 2s = 40s
const POLL_INTERVAL_MS = 2000;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [message, setMessage] = useState("Confirming your payment with Stripe…");
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!sessionId) {
      setStatus("failed");
      setMessage("No payment session found.");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      if (cancelled.current) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        const { payment_status, status: sessionStatus, order_id } = r.data || {};
        if (payment_status === "paid" && order_id) {
          clear();
          setStatus("paid");
          setMessage("Payment confirmed.");
          // Navigate to the actual order page after a short victory beat
          setTimeout(() => navigate(`/order/${order_id}`, { replace: true }), 900);
          return;
        }
        if (sessionStatus === "expired") {
          setStatus("failed");
          setMessage("Payment session expired. Please try again.");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          setMessage("Still processing. Check your email for confirmation.");
          return;
        }
        setMessage(`Confirming your payment… (${attempts}/${MAX_ATTEMPTS})`);
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("failed");
          setMessage("We could not verify your payment. Please contact us.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-40 pb-32 min-h-[70vh] flex items-center justify-center" data-testid="order-success-page">
      <Seo title="Order received" noindex />
      <div className="max-w-[640px] mx-auto px-6 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={42} strokeWidth={1.2} className="mx-auto animate-spin text-[var(--mn-ink-2)]" />
            <p className="eyebrow mt-8">One moment</p>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tighter">
              Confirming your <em className="font-medium italic">payment.</em>
            </h1>
            <p className="text-sm text-[var(--mn-ink-2)] mt-6" data-testid="order-success-status">
              {message}
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <Check size={42} strokeWidth={1.4} className="mx-auto text-[var(--mn-accent)]" />
            <p className="eyebrow mt-8 text-[var(--mn-accent)]">Confirmed</p>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tighter">
              Thank you. Taking you <em className="font-medium italic">to your order.</em>
            </h1>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <AlertTriangle size={42} strokeWidth={1.4} className="mx-auto text-[var(--mn-accent)]" />
            <p className="eyebrow mt-8">{status === "timeout" ? "Still processing" : "Issue"}</p>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tighter">
              {status === "timeout" ? "Hang tight." : "Something went sideways."}
            </h1>
            <p className="text-sm text-[var(--mn-ink-2)] mt-6" data-testid="order-success-error">{message}</p>
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <Link to="/about#concierge" className="btn-primary" data-testid="order-success-contact">Contact Us</Link>
              <Link to="/shop" className="btn-ghost">Back to Shop</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
